/**
 * The setup guide — how to start a table, and what a map has to be.
 *
 * **In the app rather than in a README**, because the person who needs it is
 * looking at the screen that needs explaining, and because half of it is
 * numbers the server actually enforces: the upload types and size, the
 * coordinate system, the fact that a waypoint is all three parts or none.
 * Written down somewhere else, those drift from the code silently.
 *
 * It deliberately states the *order* things must happen in. Most of the setup
 * surface is independently reachable, and almost every step is harmless on its
 * own — but a map with no scale costs no journey, a pin with no region can
 * never be revealed, and a party standing on an unpinned place cannot take its
 * first march at all. Each of those fails by doing nothing, which is the worst
 * way for setup to be wrong.
 */

import { Link } from 'react-router-dom';

import { MAP_IMAGE_TYPES_LABEL, MAX_MAP_UPLOAD_LABEL } from '../map/uploadMap.js';

/** The upload allowlist and ceiling, as `routes/assets.ts` enforces them. */
// Shared with the setup wizard and the map panel: three surfaces state these,
// and a limit worded differently in two of them is one somebody is told wrongly.
const IMAGE_TYPES = MAP_IMAGE_TYPES_LABEL;
const MAX_UPLOAD = MAX_MAP_UPLOAD_LABEL;
/** `routes/world-import.ts` mounts its own parser at this size. */
const MAX_IMPORT = '32 MB';

function Step({
  n,
  title,
  children,
}: {
  readonly n: number;
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="border-l-2 border-neutral-200 pl-4 dark:border-neutral-800">
      <h3 className="text-sm font-semibold">
        <span className="mr-2 text-neutral-400">{n}.</span>
        {title}
      </h3>
      <div className="mt-1 space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
        {children}
      </div>
    </section>
  );
}

function Note({ children }: { readonly children: React.ReactNode }) {
  return (
    <p className="rounded-md bg-neutral-100 px-3 py-2 text-xs text-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
      {children}
    </p>
  );
}

export function GuidePage() {
  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Starting a campaign</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          What to do, in the order it has to happen, and what the map needs to be. Setting a
          table up is the <strong>owner&rsquo;s</strong> — whoever created it — and everything
          after step&nbsp;6 is anybody&rsquo;s.
        </p>
      </header>

      <div className="space-y-5">
        <Step n={1} title="Let people in">
          <p>
            Nobody can sign in until their email is on the{' '}
            <Link to="/allowlist" className="underline underline-offset-2">
              allowlist
            </Link>
            . Add every player before the first session — it is the only gate on who can reach
            the app at all, so joining a table is a player&rsquo;s own act afterwards and needs
            no invitation.
          </p>
        </Step>

        <Step n={2} title="Roll a character">
          <p>
            Everyone, including you, makes a character before there is a table to sit at.
            Scores are rolled by the server; the classes you fail the minimums for are greyed
            out rather than hidden, so you can see what the roll cost you.
          </p>
          <p>
            Hit points and armour class are <em>never</em> typed. The server rolls first-level
            hit points from your class die and Constitution, and derives armour class from what
            you have equipped.
          </p>
          <p>
            {/* ⚠ The half of "who is this" the rules actually print. Without it
                the Dungeon Master invents a body in every scene, differently. */}
            It asks for a <strong>gender, an age, a height and a weight</strong>, and one button
            rolls the last three off the book&rsquo;s own tables. They are what the Dungeon
            Master describes you by, so a character with none of them gets described afresh
            every scene. <strong>Age is the one that costs something</strong>: the book&rsquo;s
            three aging steps are cumulative, and the wizard says what an old character is
            paying for it before you make them.
          </p>
        </Step>

        <Step n={3} title="Start the table and write the premise">
          <p>
            <Link to="/campaigns/new" className="underline underline-offset-2">
              Start a campaign
            </Link>{' '}
            takes a name, a premise and which of your characters is sitting at it. Whoever
            creates the table owns it, which is what lets you archive it, upload to it and
            import a world into it. It is not a role in the game: you play a character like
            everybody else.
          </p>
          <p>
            <strong>The premise is the entire input to world generation</strong>, so write a
            couple of sentences with places and trouble in them rather than a title. &ldquo;A
            salt road between two failing towns&rdquo; produces a world; &ldquo;fantasy
            campaign&rdquo; produces nothing worth keeping.
          </p>
        </Step>

        <Step n={4} title="Generate the world — once">
          <p>
            The button on the page you land on after creating the table writes places, people
            and factions from your premise.
          </p>
          <Note>
            <strong>It runs once and then refuses.</strong> The model is not deterministic, so a
            second run would write a <em>different</em> world whose names never collide with the
            first — leaving two incompatible accounts of one region that no single entry reveals.
            If it fails, retry: only a success closes the button.
          </Note>
          <p>
            You can edit or delete anything it wrote afterwards, and add your own. Canon marked
            private is never shown to the other players and never reaches the DM&rsquo;s prompt
            for a room-wide scene.
          </p>
        </Step>

        <Step n={5} title="Make a map">
          <p>
            The map setup page walks a new table through it in order; afterwards the same
            controls are in the map panel beside the table. {IMAGE_TYPES}, up to {MAX_UPLOAD}.
            Two kinds matter here: a <strong>world map</strong> for travel, and{' '}
            <strong>encounter maps</strong> for individual fights.
          </p>
          <p>
            There are no required dimensions and no required aspect ratio. Everything on the map
            is stored as a <strong>percentage</strong> of the image, so you can replace the
            drawing with a better one later and every pin stays where it was.
          </p>
          <Note>
            <strong>A world map and a scale are required before a new table opens.</strong> An
            older table without them keeps working and says so on the map panel — but travel
            costs nothing until both exist, which is a thing you would rather not discover in
            session three. They are two pieces of three: a march is only costed once something
            says what the <strong>ground</strong> between two places is, which means a region
            with a terrain declared or an imported world. If a march comes back costing no
            time, the panel tells you which of the three was missing.
          </Note>

          <div className="space-y-2 rounded-md border border-neutral-200 p-3 dark:border-neutral-800">
            <p className="text-sm font-medium">If you do not have a map, generate one</p>
            <p>
              <a
                href="https://azgaar.github.io/Fantasy-Map-Generator/"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                Azgaar&rsquo;s Fantasy Map Generator ↗
              </a>{' '}
              is free, runs in the browser, and is the one this application knows how to read.
              Roll a world until you like it, then take two things from it.
            </p>
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                <strong>The picture.</strong> Export the map as an image and upload it above.
                {' '}
                {IMAGE_TYPES}, up to {MAX_UPLOAD}.
              </li>
              <li>
                <strong>The scale.</strong> Azgaar shows the map&rsquo;s width in its own units —
                use that number, in miles, as the scale. It does not have to be exact; it has to
                be the distance your table agrees the map covers.
              </li>
              <li>
                <strong>Optionally, the data.</strong> Its GeoJSON exports for{' '}
                <strong>cells</strong>, <strong>rivers</strong> and <strong>routes</strong> can be
                imported below, which fills in the terrain, waterways and roads so you do not have
                to declare regions by hand.
              </li>
            </ol>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Any other generator works too, and so does a drawing — nothing about the picture is
              read by the rules. Only the GeoJSON import is Azgaar-specific.
            </p>
          </div>
        </Step>

        <Step n={6} title="Set the scale — or nothing can be travelled">
          <p>
            One number: <strong>how many miles wide the whole image is</strong>, edge to edge. The
            vertical is scaled by the same figure, so a map that is not square needs no second
            number.
          </p>
          <Note>
            Until this is set, a journey has no length. A move between two places is still
            narrated, but nothing is costed: no days pass, no getting-lost check is rolled, and
            no wandering-encounter schedule is offered. It fails by being quiet, so set it early.
          </Note>
        </Step>

        <Step n={7} title="Declare regions">
          <p>
            A region is a named area with an id you choose (<code>lowlands</code>), and it does
            two jobs at once: it is the unit <strong>fog</strong> reveals, and it is where you
            say what the ground is like.
          </p>
          <p>
            Give each one a <strong>terrain</strong> (what it costs to cross), optionally the{' '}
            <strong>surroundings</strong> (how easy it is to get lost in), and any{' '}
            <strong>obstacles</strong> — a river, a ridge, a chasm. All three lists are the
            book&rsquo;s own and are offered to you; you cannot invent a terrain the rules do not
            price.
          </p>
          <Note>
            Leave surroundings blank and they are <em>derived</em> from the terrain, which is
            usually what you want. An obstacle describes the <em>country</em>, not each crossing:
            a march that fords five rivers still crosses river country once.
          </Note>
        </Step>

        <Step n={8} title="Put places on the map">
          <p>
            A pin is three things together: an <strong>x</strong> and a <strong>y</strong> as
            percentages from 0 to 100, and the <strong>region</strong> it sits in. Click the map
            image to fill the coordinates rather than typing them.
          </p>
          <p>
            <strong>All three or none.</strong> A place with no pin is perfectly legitimate —
            most canon is not on a map — but coordinates without a region are refused, because
            fog could never hide such a pin and an unhideable waypoint is a spoiler with a dot on
            it.
          </p>
          <Note>
            <strong>The places the DM wrote have no pins.</strong> World generation writes a name
            and a description; coordinates are yours, because you are the one looking at the
            drawing. Use the dropdown above the place form to choose one and pin it — the ones
            not yet on the map are listed first.
          </Note>
          <p>
            <strong>Pin the place the party is standing at.</strong> A journey needs coordinates
            at <em>both</em> ends, and the party starts wherever world generation put them —
            which is one of those unpinned places. Miss this and the first march is silently not
            costed, with everything else looking correctly set up.
          </p>
        </Step>

        <Step n={9} title="Reveal what they have seen">
          <p>
            Fog hides waypoints by region, and it <strong>only ever uncovers</strong> — a region
            you add later is hidden by default, which is the way round a mistake should fail. You
            are exempt from your own fog, and so is the party&rsquo;s current location: they know
            where they are standing whether or not you remembered to reveal it.
          </p>
        </Step>

        <Step n={10} title="Let the players in">
          <p>
            Players join from their own lobby — your table appears there once it exists, and they
            pick which character sits at it. There is no invitation to send; the allowlist was
            the gate.
          </p>
          <Note>
            A player sees the transcript from the moment they <em>joined</em>, not from the
            beginning. Someone arriving in session four reads a catch-up digest of what they
            missed rather than four sessions of scrollback.
          </Note>
        </Step>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Importing a generated world</h2>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          If you have built a world in a map generator, you can import its GeoJSON exports —
          cells, rivers and routes — instead of declaring regions by hand. Up to {MAX_IMPORT}.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-700 dark:text-neutral-300">
          <li>
            <strong>The dry run is the default</strong>, and it runs the identical code — it
            tells you what it found and writes nothing.
          </li>
          <li>
            You are shown how the generator&rsquo;s biomes map onto the book&rsquo;s terrains{' '}
            <strong>before anything is written</strong>, and can change it. That correspondence
            is this application&rsquo;s invention, not the book&rsquo;s, so it is yours to
            approve.
          </li>
          <li>
            <strong>Importing replaces.</strong> Two sets of rivers in different places would
            leave no journey able to tell which map it is crossing.
          </li>
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Before your first session</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm text-neutral-700 dark:text-neutral-300">
          <li>
            <strong>First level is lethal, and that is the ruleset.</strong> Zero hit points is
            death here — there is no bleeding out, no death saves and no stabilising. A pair of
            first-level characters can lose to a single goblin, and often does. Frame the opening
            fight accordingly, or make the first scene something other than a fight.
          </li>
          <li>
            <strong>A first-level party cannot hire a henchman</strong> — one must stay a level
            below their patron, and there is no level-zero character. Hirelings are the answer
            for a new group: ordinary people on a wage, hired by trade.
          </li>
          <li>
            <strong>Every player has a veto.</strong> It cancels whatever the DM is writing and
            records the subject as off-limits from then on. Nothing identifies who pressed it.
            Say it exists before you start.
          </li>
          <li>
            <strong>You settle experience, not the engine.</strong> An award with no name on it
            is divided among the survivors; a named one goes to that character whole.
          </li>
        </ul>
      </section>

      <p className="text-sm text-neutral-600 dark:text-neutral-400">
        Rules questions are answered — with citations — on the{' '}
        <Link to="/rules" className="underline underline-offset-2">
          Rules Reference
        </Link>
        .
      </p>
    </div>
  );
}
